import { authFetch } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, ChevronRight, CheckCircle2, X, Eye
} from "lucide-react";
import { safeSet, safeRemove, safeGetJSON } from "../utils/safeStorage";
import { toast } from "sonner";

const API = window.location.origin;
const SESSION_KEY = "gfp_active_session";

function approxTime(mins) {
  if (!mins) return "";
  const m = Number(mins);
  if (m <= 5) return "~5 min";
  if (m <= 8) return "~5-8 min";
  if (m <= 12) return "~10 min";
  if (m <= 18) return "~15 min";
  if (m <= 25) return "~25 min";
  return `~${Math.round(m / 5) * 5} min`;
}

function saveSessionState(state) {
  safeSet(SESSION_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
}
function loadSessionState() {
  const saved = safeGetJSON(SESSION_KEY);
  if (!saved) return null;
  if (Date.now() - (saved.savedAt || 0) > 86400000) { safeRemove(SESSION_KEY); return null; }
  return saved;
}
function clearSessionState() { safeRemove(SESSION_KEY); }

const LOC_LABELS = {
  range: "Range", green: "Putting Green", short_game: "Short Game",
  course: "On Course", home: "Home", putting_green: "Putting Green"
};

const FEEDBACK_CONFIG = {
  full_swing: { fields: [
    { key: "contact", label: "Contact Quality", options: [{ value: "thin", label: "Thin" }, { value: "pure", label: "Pure" }, { value: "fat", label: "Fat" }] },
    { key: "direction", label: "Direction", options: [{ value: "left", label: "Left" }, { value: "straight", label: "Straight" }, { value: "right", label: "Right" }] },
  ]},
  short_game: { fields: [
    { key: "touch", label: "Touch", options: [{ value: "heavy", label: "Heavy" }, { value: "good", label: "Good" }, { value: "thin", label: "Thin" }] },
    { key: "distance", label: "Distance Control", options: [{ value: "short", label: "Short" }, { value: "dialed", label: "Dialed" }, { value: "long", label: "Long" }] },
  ]},
  putting: { fields: [
    { key: "speed", label: "Speed Control", options: [{ value: "short", label: "Short" }, { value: "good", label: "Good" }, { value: "long", label: "Long" }] },
    { key: "line", label: "Start Line", options: [{ value: "miss_low", label: "Miss Low" }, { value: "on_line", label: "On Line" }, { value: "miss_high", label: "Miss High" }] },
  ]},
  course: { fields: [
    { key: "strategy", label: "Course Management", options: [{ value: "poor", label: "Poor" }, { value: "ok", label: "OK" }, { value: "smart", label: "Smart" }] },
    { key: "confidence", label: "Confidence", options: [{ value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" }] },
  ]},
};

function getBlockFeedbackType(block) {
  const loc = block?.location;
  if (loc === "green" || loc === "putting_green") return "putting";
  if (loc === "short_game") return "short_game";
  if (loc === "course") return "course";
  return "full_swing";
}

export default function Session() {
  const navigate = useNavigate();
  const { missionId } = useParams();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [phase, setPhase] = useState("plan"); // plan | practicing | session_reflection | session_complete
  const [blockFeedbackData, setBlockFeedbackData] = useState({});
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [sessionReflection, setSessionReflection] = useState({});
  const [showQuickRecap, setShowQuickRecap] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await authFetch(`${API}/api/missions/${missionId}`);
        if (res.ok) {
          const data = await res.json();
          setMission(data);
          const saved = loadSessionState();
          if (saved && saved.missionId === missionId) {
            setCurrentBlockIndex(saved.currentBlockIndex || 0);
            setBlockFeedbackData(saved.blockFeedbackData || {});
            setPhase(saved.phase === "session_complete" ? "plan" : (saved.phase || "plan"));
          }
        } else { setError("Session not found"); }
      } catch (e) { setError("Failed to load session."); }
      finally { setLoading(false); }
    };
    init();
  }, [missionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mission) return;
    if (phase === "plan" || phase === "session_complete") return;
    saveSessionState({ missionId, currentBlockIndex, blockFeedbackData, phase });
  }, [missionId, currentBlockIndex, blockFeedbackData, phase, mission]);

  const blocks = mission?.session_blocks || [];
  const currentBlock = blocks[currentBlockIndex] || null;
  const blockDrills = currentBlock?.drills || [];
  const totalBlocks = blocks.length;

  const handleExitSession = () => {
    if (window.confirm("Your progress is saved. You can resume later.")) navigate("/dashboard");
  };

  const handleBlockComplete = () => setShowFeedbackModal(true);

  const handleBlockFeedback = (feedback) => {
    const fbType = getBlockFeedbackType(currentBlock);
    setBlockFeedbackData(prev => ({
      ...prev,
      [`block_${currentBlockIndex}`]: { ...feedback, category: fbType, location: currentBlock?.location, block_index: currentBlockIndex }
    }));
    setShowFeedbackModal(false);
    if (currentBlockIndex < totalBlocks - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1);
      setPhase("plan");
    } else {
      setPhase("session_reflection");
    }
  };

  const handleSkipFeedback = () => {
    setShowFeedbackModal(false);
    if (currentBlockIndex < totalBlocks - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1);
      setPhase("plan");
    } else {
      setPhase("session_reflection");
    }
  };

  const completeSession = async (reflection = {}) => {
    setPhase("session_complete");
    clearSessionState();
    try {
      await authFetch(`${API}/api/sessions/${missionId}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ block_feedback: blockFeedbackData, session_reflection: reflection })
      });
    } catch (e) { console.error(e); }
  };

  // ── ERROR ──
  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6" data-testid="session-error">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Something went wrong</h2>
          <p className="text-gray-600 text-sm mb-6 font-body">{error}</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full h-14 bg-green-900 hover:bg-green-800 text-white font-bold rounded-xl font-heading" data-testid="session-error-dashboard">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // ── LOADING ──
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center" data-testid="session-loading">
        <div className="w-10 h-10 border-2 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── SESSION REFLECTION ──
  if (phase === "session_reflection") {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
        <div className="max-w-md w-full animate-fade-in">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-900" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-1 font-heading" data-testid="reflection-title">Nice work.</h1>
            <p className="text-gray-600 text-sm font-body">Quick reflection</p>
          </div>
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 space-y-6 mb-6">
            {[
              { key: "contact", label: "Contact Quality", opts: ["Thin", "Pure", "Fat"] },
              { key: "direction", label: "Direction", opts: ["Left", "Straight", "Right"] },
              { key: "confidence", label: "Confidence", opts: ["Low", "Medium", "High"] },
            ].map(f => (
              <div key={f.key}>
                <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-3 font-body">{f.label}</p>
                <div className="flex gap-2">
                  {f.opts.map(o => (
                    <button key={o} onClick={() => setSessionReflection(prev => ({ ...prev, [f.key]: o.toLowerCase() }))}
                      className={`flex-1 h-12 rounded-xl border-2 text-sm font-bold font-heading transition-all ${
                        sessionReflection[f.key] === o.toLowerCase() ? "bg-green-50 border-green-900 text-green-900" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                      }`} data-testid={`reflection-${f.key}-${o.toLowerCase()}`}>{o}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => completeSession({})} className="text-gray-600 text-sm px-4 py-3 font-body" data-testid="reflection-skip-btn">Skip</button>
            <Button onClick={() => completeSession(sessionReflection)} className="flex-1 h-16 bg-green-900 hover:bg-green-800 text-white text-lg font-bold rounded-xl font-heading" data-testid="reflection-submit-btn">
              Complete Session
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── SESSION COMPLETE ──
  if (phase === "session_complete") {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center animate-fade-in">
          <div className="w-20 h-20 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-900" />
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2 font-heading" data-testid="session-done-msg">Done.</h1>
          <p className="text-gray-600 text-sm mb-8 font-body">{mission?.title}</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full h-16 bg-green-900 hover:bg-green-800 text-white text-lg font-bold rounded-xl font-heading" data-testid="session-done-btn">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // ── PLAN VIEW — Trust Summary + Visual Hierarchy ──
  if (phase === "plan") {
    // Calculate trust totals per block
    const rangeTotals = blocks.filter(b => b.location === "range").map(b => ({
      balls: b.balls,
      drillSum: (b.drills || []).reduce((s, d) => s + (d.balls || 0), 0),
      skill: b.primary_skill
    }));
    const totalBalls = rangeTotals.reduce((s, r) => s + (r.balls || 0), 0);
    const totalDrillBalls = rangeTotals.reduce((s, r) => s + r.drillSum, 0);
    const totalMins = blocks.filter(b => b.location !== "range" && b.location !== "course").reduce((s, b) => s + (b.duration_minutes || 0), 0);
    const totalHoles = blocks.filter(b => b.location === "course").reduce((s, b) => s + (b.holes || 0), 0);

    // Estimate total session time
    const BALL_TIME = { 30: [20,30], 40: [30,40], 50: [40,50], 60: [50,60], 80: [65,75], 100: [80,95] };
    const estBallMin = rangeTotals.reduce((s, r) => s + (BALL_TIME[r.balls]?.[0] || Math.round((r.balls||0)*0.65)), 0);
    const estBallMax = rangeTotals.reduce((s, r) => s + (BALL_TIME[r.balls]?.[1] || Math.round((r.balls||0)*0.95)), 0);
    const estHoleMin = totalHoles * 10;
    const estHoleMax = totalHoles * 15;
    const estTotalMin = estBallMin + totalMins + estHoleMin;
    const estTotalMax = estBallMax + totalMins + estHoleMax;
    const estLabel = estTotalMin === estTotalMax ? `~${estTotalMin} min` : `~${estTotalMin}–${estTotalMax} min`;

    // Phase type styling
    const phaseStyle = (phaseName) => {
      const p = (phaseName || "").toLowerCase();
      if (p.includes("loosen") || p.includes("warm")) return { border: "border-gray-300", label: "text-gray-400", weight: "font-medium" };
      if (p.includes("pressure") || p.includes("scoring") || p.includes("fairway")) return { border: "border-green-900", label: "text-green-800", weight: "font-extrabold" };
      return { border: "border-green-700", label: "text-gray-500", weight: "font-bold" };
    };

    return (
      <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
        <header className="px-5 py-4 border-b-2 border-gray-200 bg-white">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <button onClick={handleExitSession} className="p-1 text-gray-600 hover:text-gray-900" data-testid="exit-session">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <p className="text-sm font-bold text-gray-900 font-heading">{mission?.title}</p>
            <button onClick={handleExitSession} className="p-1 text-gray-600 hover:text-gray-900">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <main className="flex-1 px-5 py-6 overflow-y-auto">
          <div className="max-w-md mx-auto">

            {/* ── TRUST SUMMARY ── */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 mb-6" data-testid="trust-summary">
              <h2 className="text-2xl font-extrabold text-gray-900 font-heading mb-4" data-testid="session-overview-title">{"Today\u2019s Session"}</h2>
              
              {/* Block summary line items */}
              <div className="space-y-2 mb-4">
                {blocks.map((block, bi) => (
                  <div key={bi} className="flex items-center justify-between" data-testid={`summary-line-${bi}`}>
                    <span className="text-sm text-gray-700 font-body">{LOC_LABELS[block.location] || block.location}</span>
                    <span className="text-sm font-bold text-gray-900 font-heading">
                      {block.balls ? `${block.balls} balls` : block.holes ? `${block.holes} holes` : `${block.duration_minutes} min`}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t-2 border-gray-100 pt-3">
                {totalBalls > 0 && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold tracking-widest uppercase text-gray-500 font-body">Total Balls</span>
                    <span className={`text-sm font-extrabold font-heading ${totalDrillBalls === totalBalls ? "text-green-900" : "text-red-600"}`} data-testid="trust-ball-total">
                      {totalDrillBalls} / {totalBalls} {totalDrillBalls === totalBalls ? "✓" : "✗"}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold tracking-widest uppercase text-gray-500 font-body">Estimated Time</span>
                  <span className="text-sm font-bold text-gray-900 font-heading" data-testid="trust-est-time">{estLabel}</span>
                </div>
              </div>
            </div>

            {/* ── SESSION INTELLIGENCE ── */}
            {mission?.personalisation?.explanation && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6" data-testid="session-explanation">
                <p className="text-xs font-bold tracking-widest uppercase text-green-800 mb-1 font-body">{"Today\u2019s Focus"}</p>
                <p className="text-green-900 text-sm font-body">{mission.personalisation.explanation}</p>
              </div>
            )}

            {/* ── DRILL BREAKDOWN ── */}
            <div className="space-y-4 mb-8">
              {blocks.map((block, bi) => {
                const drills = block.drills || [];
                const isDone = bi < currentBlockIndex;
                return (
                  <div key={bi} className={`bg-white border-2 rounded-2xl overflow-hidden transition-all ${isDone ? "border-green-200 opacity-50" : "border-gray-200"}`} data-testid={`plan-block-${bi}`}>
                    <div className="px-5 pt-5 pb-3 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-extrabold font-heading shrink-0 ${isDone ? "bg-green-100 text-green-900" : "bg-gray-100 text-gray-900"}`}>
                        {isDone ? <CheckCircle2 className="w-5 h-5" /> : bi + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 font-heading">{LOC_LABELS[block.location] || block.location}</h3>
                        <p className="text-xs text-gray-600 font-body">
                          {block.balls ? `${block.balls} balls` : block.holes ? `${block.holes} holes` : `${block.duration_minutes} min`}
                        </p>
                      </div>
                    </div>

                    <div className="px-5 pb-5 space-y-3">
                      {drills.map((d, di) => {
                        const style = phaseStyle(d.phase_name || d.block_type);
                        return (
                          <div key={di} className={`border-l-[3px] ${style.border} pl-4`} data-testid={`drill-${bi}-${di}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className={`text-[10px] font-bold tracking-widest uppercase ${style.label} mb-0.5 font-body`}>
                                  {d.phase_name || d.block_type}
                                  {d.is_coach_drill && <span className="ml-2 text-green-700">COACH</span>}
                                </p>
                                <p className={`text-base ${style.weight} text-gray-900 font-heading leading-tight`}>{d.name}</p>
                              </div>
                              {d.balls && (
                                <span className="shrink-0 text-sm font-bold text-green-900 bg-green-50 border border-green-200 px-2 py-0.5 rounded-lg font-heading" data-testid={`drill-balls-${bi}-${di}`}>
                                  {d.balls}
                                </span>
                              )}
                            </div>
                            {d.finish_goal && (
                              <p className="text-xs text-gray-500 font-body mt-1">{d.finish_goal}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <Button onClick={() => setPhase("practicing")}
              className="w-full h-16 bg-green-900 hover:bg-green-800 text-white text-xl font-bold rounded-xl font-heading active:scale-95 transition-transform"
              data-testid="start-session-btn">
              Begin Session <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // ── PRACTICING VIEW — Current block with all drills visible ──
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      {/* Header */}
      <header className="px-5 py-3 border-b-2 border-gray-200 bg-white">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-2">
            <button onClick={handleExitSession} className="p-1 text-gray-600 hover:text-gray-900" data-testid="practicing-exit-back"><ArrowLeft className="w-5 h-5" /></button>
            <span className="text-xs font-bold tracking-widest uppercase text-gray-500 font-body">Block {currentBlockIndex + 1} of {totalBlocks}</span>
            <button onClick={handleExitSession} className="p-1 text-gray-600 hover:text-gray-900" data-testid="practicing-exit-close"><X className="w-5 h-5" /></button>
          </div>
          {/* Progress */}
          <div className="flex gap-1.5">
            {blocks.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full ${i < currentBlockIndex ? "bg-green-900" : i === currentBlockIndex ? "bg-green-600" : "bg-gray-200"}`} />
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 px-5 py-6 overflow-y-auto">
        <div className="max-w-md mx-auto">
          {/* Block info */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-extrabold text-gray-900 font-heading" data-testid="block-location">
              {LOC_LABELS[currentBlock?.location] || currentBlock?.location}
            </h2>
            <p className="text-sm text-gray-600 font-body mt-1">
              {currentBlock?.location === "course" ? `${currentBlock?.holes} holes`
                : currentBlock?.balls ? `${currentBlock.balls} balls` : approxTime(currentBlock?.duration_minutes)}
            </p>
            {currentBlock?.format_name && (
              <span className="inline-block mt-2 text-xs font-bold bg-green-50 text-green-900 border border-green-200 px-3 py-1 rounded-lg" data-testid="block-format-label">
                {currentBlock.format_name}
              </span>
            )}
          </div>

          {/* Drills — glanceable, large, one per phase */}
          <div className="space-y-4 mb-8" data-testid="block-drills">
            {blockDrills.map((d, di) => (
              <div key={di} className="bg-white border-2 border-gray-200 rounded-2xl p-5" data-testid={`practice-drill-${di}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1 font-body">
                      {d.phase_name || d.block_type}
                      {d.is_coach_drill && <span className="ml-2 text-green-700">COACH</span>}
                    </p>
                    <p className="text-2xl font-extrabold text-gray-900 font-heading leading-tight">{d.name}</p>
                  </div>
                  {d.balls && (
                    <span className="shrink-0 text-base font-bold text-green-900 bg-green-50 border border-green-200 px-3 py-1 rounded-lg font-heading" data-testid={`practice-drill-balls-${di}`}>
                      {d.balls} balls
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 font-body">
                  {d.distance && d.distance !== "Varies" ? d.distance : ""}{d.distance && d.format ? " — " : ""}{d.format || ""}
                </p>
                {d.finish_goal && (
                  <div className="mt-3 bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3">
                    <p className="text-sm font-bold text-gray-900 font-heading">{d.finish_goal}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button onClick={handleBlockComplete}
            className="w-full h-16 bg-green-900 hover:bg-green-800 text-white text-lg font-bold rounded-xl font-heading active:scale-95 transition-transform"
            data-testid="block-complete-btn">
            <CheckCircle2 className="w-5 h-5 mr-2" /> Block Complete
          </Button>
        </div>
      </main>

      {/* Quick Recap FAB */}
      {showQuickRecap && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6" onClick={() => setShowQuickRecap(false)} data-testid="quick-recap-overlay">
          <div className="max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h2 className="text-white font-extrabold text-xl text-center mb-6 font-heading" data-testid="recap-block-name">
              {LOC_LABELS[currentBlock?.location] || currentBlock?.location}
            </h2>
            <div className="space-y-4">
              {blockDrills.map((d, di) => (
                <div key={di} className="text-center" data-testid={`recap-drill-${di}`}>
                  <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase font-body">{d.phase_name || d.block_type}</p>
                  <p className="text-white text-lg font-bold font-heading">{d.name}</p>
                  {d.finish_goal && <p className="text-gray-300 text-xs font-body">{d.finish_goal}</p>}
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-xs text-center mt-8 font-body">Tap anywhere to dismiss</p>
          </div>
        </div>
      )}

      {/* Quick Recap FAB button */}
      <button onClick={() => setShowQuickRecap(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-900 rounded-2xl shadow-lg flex items-center justify-center z-40 active:scale-95 transition-transform"
        data-testid="quick-recap-fab">
        <Eye className="w-6 h-6 text-white" />
      </button>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          title={`${LOC_LABELS[currentBlock?.location] || currentBlock?.location}`}
          subtitle="How did that go?"
          fbType={getBlockFeedbackType(currentBlock)}
          onSubmit={handleBlockFeedback}
          onSkip={handleSkipFeedback}
          testIdPrefix="block-feedback"
        />
      )}
    </div>
  );
}

function FeedbackModal({ title, subtitle, fbType, onSubmit, onSkip, testIdPrefix }) {
  const ratings = [
    { value: "poor", label: "Poor", color: "border-red-300 text-red-700 hover:bg-red-50" },
    { value: "ok", label: "OK", color: "border-amber-300 text-amber-700 hover:bg-amber-50" },
    { value: "good", label: "Good", color: "border-green-300 text-green-700 hover:bg-green-50" },
    { value: "great", label: "Great", color: "border-green-600 text-green-900 hover:bg-green-50" },
  ];
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4" data-testid={`${testIdPrefix}-modal`}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md border-2 border-gray-200 animate-fade-in">
        <h3 className="text-xl font-bold text-gray-900 mb-1 font-heading">{title}</h3>
        <p className="text-gray-600 text-sm mb-5 font-body">{subtitle}</p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {ratings.map(r => (
            <button key={r.value}
              onClick={() => onSubmit({ rating: r.value, category: fbType })}
              className={`h-14 rounded-xl border-2 text-sm font-bold font-heading transition-all active:scale-95 ${r.color}`}
              data-testid={`${testIdPrefix}-rating-${r.value}`}>
              {r.label}
            </button>
          ))}
        </div>
        <button onClick={onSkip} className="w-full text-gray-500 text-xs py-2 font-body" data-testid={`${testIdPrefix}-skip-btn`}>Skip</button>
      </div>
    </div>
  );
}
